import { AttachmentUrlIcon, LightBox } from "@/components";
import useChatStore from "@/store/chatStore";
import { checkVideoFromMimeType, cn } from "@/utils/functions";
import React, { useEffect, useState } from "react";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

type PreviewAttachmentLightBoxProps = {};

const PreviewAttachmentLightBox: React.FC<
  PreviewAttachmentLightBoxProps
> = ({}) => {
  // internal current index
  const [internalCurrentIndex, setCurrentIndex] = useState<number | null>(null);

  const { attachments, setAttachment } = useChatStore();

  // Update current index state when attachments in store change
  useEffect(() => {
    setCurrentIndex(attachments?.currentIndex ?? null);
  }, [attachments]);

  const attachmentList = attachments?.attachmentList || [];
  const isOpen = attachments !== null && attachmentList.length > 0;
  const currentAttachment = isOpen
    ? attachmentList[internalCurrentIndex!]
    : null;

  // handle open/close lightbox for attachments
  const handleLightBoxOpenChange = (open: boolean) => {
    if (!open) {
      setAttachment(null);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (
      internalCurrentIndex !== null &&
      internalCurrentIndex < attachmentList.length - 1
    ) {
      setCurrentIndex(internalCurrentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (internalCurrentIndex !== null && internalCurrentIndex > 0) {
      setCurrentIndex(internalCurrentIndex - 1);
    }
  };

  const handleSelectAttachment = (index: number) => {
    setCurrentIndex(index);
  };

  const canGoPrev = internalCurrentIndex !== null && internalCurrentIndex > 0;
  const canGoNext =
    internalCurrentIndex !== null &&
    internalCurrentIndex < attachmentList.length - 1;

  return (
    <LightBox
      open={isOpen}
      onOpenChange={handleLightBoxOpenChange}
      renderHeader={() => (
        <div className="flex items-center justify-between w-full px-6 py-4">
          {/* File name */}
          <div
            className="text-sm text-dark_primary w-52 truncate"
            title={currentAttachment?.name}
          >
            {currentAttachment?.name}
          </div>

          {/* Counter */}
          <div className="text-sm text-light_default">
            {internalCurrentIndex !== null &&
              `${internalCurrentIndex + 1} / ${attachmentList.length}`}
          </div>
        </div>
      )}
    >
      <div
        className="relative w-full h-full flex items-center justify-center"
        onKeyDown={(e) => {
          /* Keyboard navigation */
          if (e.key === "ArrowLeft" && canGoPrev) handlePrev();
          if (e.key === "ArrowRight" && canGoNext) handleNext();
          if (e.key === "Escape") handleLightBoxOpenChange(false);
        }}
        tabIndex={0}
      >
        {/* Previous button */}
        {canGoPrev && (
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
          >
            <IoChevronBack className="w-6 h-6" />
          </button>
        )}

        {/* Next button */}
        {canGoNext && (
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
          >
            <IoChevronForward className="w-6 h-6" />
          </button>
        )}

        {/* Main content */}
        {currentAttachment && (
          <AttachmentUrlIcon
            url={currentAttachment.url}
            className="w-auto h-auto rounded-none max-w-full max-h-full"
            mimeType={currentAttachment.type || ""}
            name={currentAttachment.name || ""}
            render={({ mimeType, url, className }) => {
              if (checkVideoFromMimeType(mimeType)) {
                return (
                  <video
                    key={currentAttachment.url} // Force re-render when changing videos
                    src={url}
                    className={cn("object-contain", className)}
                    controls
                  >
                    ビデオの読み込みに失敗しました。
                  </video>
                );
              }
            }}
          />
        )}

        {/* Attachment thumbnails at bottom */}
        {attachmentList.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm rounded-lg p-2">
            <div className="flex gap-2 max-w-96 overflow-x-auto no-scrollbar">
              {attachmentList.map((attachment, index) => (
                <button
                  key={`${attachment.url}-${index}`}
                  onClick={() => handleSelectAttachment(index)}
                  className={cn(
                    "w-12 h-12 rounded-md overflow-hidden border-2 transition-all flex-shrink-0",
                    index === internalCurrentIndex
                      ? "border-cyan"
                      : "border-transparent hover:border-white/50"
                  )}
                >
                  <AttachmentUrlIcon
                    url={attachment.url}
                    className="w-full h-full object-cover"
                    mimeType={attachment.type || ""}
                    name={attachment.name || ""}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </LightBox>
  );
};

export default PreviewAttachmentLightBox;
